'use client'

import { useState } from 'react'
import { getLocalePrefix, toPublicLocale } from '@/lib/locale-prefix'
import Link from 'next/link'
import { Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LanguageSelector } from '@/components/language-selector'
import { useLocale, useTranslations } from 'next-intl'
import { getBrand } from '@/lib/brands'
import { getCanonicalFamilyLandingHref } from '@/lib/locale-paths'
import { BrandNavLogo } from '@/components/brand-nav-logo'

export function LegalNav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const locale = useLocale()
  const t = useTranslations('landing.navigation')
  const brand = getBrand()

  // Create locale-aware URLs
  const getLocalizedUrl = (path: string) => {
    return `${getLocalePrefix(toPublicLocale(locale))}${path}`
  }

  const navLinks = [
    { href: getLocalizedUrl('/#pricing'), label: t('pricing') },
    { href: getLocalizedUrl('/#how-it-works'), label: t('howItWorks') },
    { href: getLocalizedUrl('/#faq'), label: t('faq') },
    ...(brand.id === 'raton-perez'
      ? [{ href: getCanonicalFamilyLandingHref('santa', toPublicLocale(locale)), label: t('otherCharacters') }]
      : []),
  ]

  return (
    <nav className="relative z-20 border-b border-white/10 bg-black/20 backdrop-blur-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 md:px-10">
        {/* Logo */}
        <Link href={getLocalizedUrl('/')} className="flex items-center gap-2 md:gap-3">
          <BrandNavLogo />
          <span className="text-lg font-bold md:text-xl">{brand.name}</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-6 text-sm lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="transition-colors hover:text-amber-200"
            >
              {link.label}
            </Link>
          ))}
          <LanguageSelector mode="public" />
        </div>

        {/* Mobile actions */}
        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSelector mode="public" variant="compact" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="border-t border-white/10 bg-black/30 backdrop-blur-md lg:hidden">
          <div className="mx-auto max-w-7xl px-6 py-4">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-base transition-colors hover:text-amber-200"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
